import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ADMIN_FORMULARIOS_MOCK } from '../mocks/admin-formularios.mock';
import {
  CampoConfigurableFormularioAdmin,
  ConfiguracionFormularioAdmin,
  EstadoFormularioAdmin,
  FechasFormularioAdmin,
  FormularioConcursoAdmin,
  SeccionFormularioAdmin,
} from '../models/admin-formularios.models';

@Injectable({ providedIn: 'root' })
export class AdminFormulariosService {
  private readonly formularios$ = new BehaviorSubject<FormularioConcursoAdmin[]>(ADMIN_FORMULARIOS_MOCK);

  getFormularios(): Observable<FormularioConcursoAdmin[]> {
    return this.formularios$.asObservable();
  }

  getFormularioById(id: string): Observable<FormularioConcursoAdmin | null> {
    return of(this.formularios$.value.find((item) => item.id === id) ?? null);
  }

  getFormularioPorConcurso(concurso: string): Observable<FormularioConcursoAdmin | null> {
    const concursoNormalizado = this.normalizarTexto(concurso);
    return of(
      this.formularios$.value.find((item) => {
        const nombre = this.normalizarTexto(item.concurso);
        return nombre === concursoNormalizado || nombre.includes(concursoNormalizado) || concursoNormalizado.includes(nombre);
      }) ?? null
    );
  }

  crearFormularioBase(): FormularioConcursoAdmin {
    const timestamp = new Date().toISOString().slice(0, 10);
    return {
      id: `FORM-${Date.now()}`,
      nombreFormulario: '',
      codigoFormulario: '',
      concurso: '',
      subdireccion: '',
      convocatoria: '',
      anio: new Date().getFullYear(),
      descripcion: '',
      instruccionesPostulantes: '',
      mensajeInformativoSistema: '',
      estado: 'borrador',
      fechaCreacion: timestamp,
      secciones: this.seccionesBase(),
      configuracion: this.configuracionBase(),
      fechas: this.fechasBase(),
    };
  }

  guardarFormulario(formulario: FormularioConcursoAdmin, estado: EstadoFormularioAdmin): Observable<FormularioConcursoAdmin> {
    const actualizado: FormularioConcursoAdmin = {
      ...formulario,
      estado,
      fechaActualizacion: new Date().toISOString(),
    };

    const existentes = this.formularios$.value;
    const indice = existentes.findIndex((item) => item.id === actualizado.id);
    const siguiente =
      indice >= 0
        ? existentes.map((item, posicion) => (posicion === indice ? actualizado : item))
        : [actualizado, ...existentes];

    this.formularios$.next(siguiente);
    return of(actualizado);
  }

  private seccionesBase(): SeccionFormularioAdmin[] {
    return [
      { id: 'SEC-1', titulo: 'Informacion general del proyecto', descripcion: 'Descripcion y antecedentes generales.' },
      { id: 'SEC-2', titulo: 'Instituciones y equipo de trabajo', descripcion: 'Instituciones participantes y equipo.' },
      { id: 'SEC-3', titulo: 'Presupuesto', descripcion: 'Detalle de los gastos y presupuesto solicitado.' },
      { id: 'SEC-4', titulo: 'Documentos', descripcion: 'Documentos requeridos para la postulacion.' },
      { id: 'SEC-5', titulo: 'Declaraciones y autorizaciones', descripcion: 'Declaraciones juradas y autorizaciones.' },
    ];
  }

  private configuracionBase(): ConfiguracionFormularioAdmin {
    return {
      guardadoParcial: true,
      cargaArchivos: true,
      correoConfirmacion: true,
      mostrarFolio: true,
      validacionesPersonalizadas: false,
      campos: this.camposBase(),
    };
  }

  private camposBase(): CampoConfigurableFormularioAdmin[] {
    return [
      { id: 'titulo', label: 'Titulo del proyecto', obligatorio: true },
      { id: 'resumen', label: 'Resumen ejecutivo', obligatorio: true },
      { id: 'area-ocde', label: 'Area OCDE', obligatorio: true },
      { id: 'disciplina', label: 'Disciplina', obligatorio: true },
      { id: 'subdisciplina', label: 'Subdisciplina', obligatorio: false },
      { id: 'duracion', label: 'Duracion del proyecto', obligatorio: true },
      { id: 'presupuesto', label: 'Presupuesto', obligatorio: true },
    ];
  }

  private fechasBase(): FechasFormularioAdmin {
    return {
      inicioPostulacion: '',
      cierrePostulacion: '',
      inicioEvaluacion: '',
      cierreEvaluacion: '',
      programarPublicacion: false,
      fechaPublicacion: '',
      horaPublicacion: '',
    };
  }

  private normalizarTexto(valor: string): string {
    return valor
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .trim()
      .toLowerCase();
  }
}
